import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

import { NAV_ITEMS } from '@/config/routes'
import { useAuth } from '@/providers/AuthProvider'
import { usePermissions } from '@/contexts/PermissionContext'

interface SidebarNavProps {
  onNavigate?: () => void
}

const isActivePath = (currentPath: string, targetPath: string) => {
  if (targetPath === '/') {
    return currentPath === '/'
  }

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
}

export const SidebarNav = ({ onNavigate }: SidebarNavProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasRole } = useAuth()
  const { hasPermission } = usePermissions()

  const items = NAV_ITEMS.filter((item) => {
    // Check permission-based access first
    if (item.permission) {
      return hasPermission(item.permission)
    }
    // Fall back to role-based access
    if (item.roles) {
      return hasRole(item.roles)
    }
    // If neither is specified, show the item
    return true
  })

  return (
    <Stack spacing={2} sx={{ px: 1 }}>
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          KiroSupport
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Admin Console
        </Typography>
      </Box>
      <List sx={{ py: 0 }}>
        {items.map((item) => {
          const Icon = item.icon
          const active = isActivePath(location.pathname, item.to)

          return (
            <ListItemButton
              key={item.key}
              selected={active}
              onClick={() => {
                navigate(item.to)
                onNavigate?.()
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '.MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 40, color: active ? 'inherit' : 'text.secondary' }}
              >
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          )
        })}
      </List>
    </Stack>
  )
}
